import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { HANDLE_ERROR_AND_THROW } from '@helios/utils'
import { Observable, of } from 'rxjs'
import { map } from 'rxjs/operators'
import { SourceKey } from '../models/source-key.model'
import {
  DataRequest,
  GridResponse,
  QuerySort,
  REQUIRED_FIELDS,
  RequestFilter,
} from '../models/transactions-query.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import { rowsFromApi } from '../grid/row.adapter'
import { WorkbenchDataSource } from './workbench-data-source'

/**
 * Read-API endpoints, one per source. Resolved against `<base href="/helios/">`. The
 * flex-transactions service is on its own host, so for local beta dev a
 * `/helios/helios-flex-transactions/**` proxy entry must be added ahead of the generic
 * `/helios/helios-*` catch-all — but that is a local-dev-only edit that is NOT committed
 * (CLAUDE.md: "Remember to revert proxy.conf.beta.json before committing"), same as the field
 * catalog. Clearwater has no endpoint.
 */
export const QUERY_URL: Partial<Record<SourceKey, string>> = {
  api: 'helios-flex-transactions/flex-transactions/query',
  prom: 'helios-flex-transactions/flex-external-transactions/query',
}

/**
 * How many rows to pull in one request.
 *
 * The response carries no total-row count, so an ag-Grid server-side row model would have nothing to
 * page against and would break the cross-grid matching, cross-filtering and status-bar totals that
 * all read the whole row set. So this stays a single bounded page into the client-side model.
 *
 * As of PVAL-26129 the page is the correct one for the WHOLE SESSION rather than an arbitrary first
 * slice: the applied filter and sort are sent with the query, so the server decides which rows and in
 * what order. The bound still matters — a session with more than PAGE_SIZE matching rows is truncated,
 * and the client-only surfaces (status chips, quick filters, cross-filter, quick-search) can only ever
 * see what is inside it. Removing the bound needs paging / a server-side row model.
 */
export const PAGE_SIZE = 5000

@Injectable()
export class HttpWorkbenchDataSource extends WorkbenchDataSource {
  constructor(private readonly http: HttpClient) {
    super()
  }

  /**
   * @param source which grid source to query
   * @param fields field names to select
   * @param filter the two-bucket filter payload, or null when nothing is applied
   * @param sort the sort array in precedence order, or null to take the endpoint's default order
   */
  load(
    source: SourceKey,
    fields: string[],
    filter: RequestFilter | null = null,
    sort: QuerySort[] | null = null,
  ): Observable<WorkbenchRow[]> {
    const url = QUERY_URL[source]
    // Clearwater has no read endpoint, and with no catalog there are no fields worth asking for.
    if (!url || !fields.length) return of<WorkbenchRow[]>([])

    const body: DataRequest = {
      // Always 0: the panel holds one bounded page, and any filter or sort change re-fetches from the
      // top rather than paging into a result set that no longer exists.
      startRow: 0,
      endRow: PAGE_SIZE,
      fields: this.selection(source, fields),
      sort,
      filter,
      // Accepted by the schema but not implemented server-side.
      group: null,
    }

    return this.http
      .post<GridResponse>(url, body, {
        // Without this, the app-wide ErrorInterceptor reports a failed request and then swallows it
        // into `EMPTY` — no `next`, no `error`, the Observable just completes. The grid's own failure
        // handling (toast, empty table, Retry, Reset to Text) never runs, and the loading overlay it
        // raised for this request is never taken down. positions-2 hit the same trap.
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      })
      .pipe(map(response => rowsFromApi(source, response?.data ?? [])))
  }

  /**
   * The catalog's fields plus the ids the workbench always needs, de-duplicated. A name the server
   * does not recognise comes back as null per row rather than erroring, so over-asking is safe;
   * under-asking silently loses a column.
   */
  private selection(source: SourceKey, fields: string[]): string[] {
    const required =
      source === 'api'
        ? REQUIRED_FIELDS.api
        : source === 'prom'
        ? REQUIRED_FIELDS.prom
        : []
    return [...new Set([...required, ...fields])]
  }
}
